import React, { useEffect, useState } from 'react';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { Copy, Loader2, RefreshCw } from 'lucide-react';
import { Button } from './ui/button';
import { superadminApi, apiErrorMessage } from '../lib/api';

/**
 * Superadmin → Interest list: everyone who typed their email into the pre-launch page on
 * holidayhive.co.uk (12/09/2026). WORKING-WITH-ROB.md rule 9 — a list Rob will want to
 * look at needs a screen, not a database query.
 *
 * Kept to what he actually does with it: see who is new, copy the addresses into an
 * email, and tick people off as he replies.
 */
const InterestList = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await superadminApi.listInterest();
      setRows(res.data || []);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Couldn't load the interest list"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const toggle = async (row) => {
    setBusy(row.id);
    try {
      const res = await superadminApi.setInterestContacted(row.id, !row.contacted_at);
      setRows((prev) => prev.map((r) => (r.id === row.id ? { ...r, contacted_at: res.data.contacted_at } : r)));
    } catch (e) {
      toast.error(apiErrorMessage(e, "Couldn't update that row"));
    } finally {
      setBusy(null);
    }
  };

  const copyAll = async () => {
    const waiting = rows.filter((r) => !r.contacted_at).map((r) => r.email);
    try {
      await navigator.clipboard.writeText(waiting.join(', '));
      toast.success(`Copied ${waiting.length} address${waiting.length === 1 ? '' : 'es'} still waiting`);
    } catch (_) {
      toast.error("Couldn't copy — your browser blocked clipboard access");
    }
  };

  const waiting = rows.filter((r) => !r.contacted_at).length;

  return (
    <div className="space-y-4" data-testid="interest-list">
      <div className="flex flex-wrap items-center gap-3">
        <div className="text-sm text-muted-foreground">
          {loading ? 'Loading…' : `${rows.length} registered · ${waiting} still waiting for a reply`}
        </div>
        <div className="ml-auto flex gap-2">
          <Button variant="outline" size="sm" onClick={copyAll} disabled={loading || waiting === 0}>
            <Copy className="mr-2 h-4 w-4" /> Copy waiting addresses
          </Button>
          <Button variant="outline" size="sm" onClick={load} disabled={loading}>
            <RefreshCw className="mr-2 h-4 w-4" /> Refresh
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 py-10 text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
      ) : rows.length === 0 ? (
        <div className="rounded-lg border border-dashed p-8 text-center text-sm text-muted-foreground">
          Nobody has registered yet. The form is at holidayhive.co.uk.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-4 py-3">Email</th>
                <th className="px-4 py-3">Registered</th>
                <th className="px-4 py-3">From</th>
                <th className="px-4 py-3">Replied?</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t">
                  <td className="px-4 py-3 font-medium">
                    <a href={`mailto:${r.email}`} className="hover:underline">{r.email}</a>
                  </td>
                  <td className="px-4 py-3 text-muted-foreground" style={{ fontVariantNumeric: 'tabular-nums' }}>
                    {r.created_at ? format(new Date(r.created_at), 'dd/MM/yyyy HH:mm') : '—'}
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">{r.source_host || '—'}</td>
                  <td className="px-4 py-3">
                    <label className="inline-flex cursor-pointer items-center gap-2">
                      <input
                        type="checkbox"
                        checked={!!r.contacted_at}
                        disabled={busy === r.id}
                        onChange={() => toggle(r)}
                        className="h-4 w-4"
                      />
                      <span className="text-muted-foreground">
                        {r.contacted_at ? format(new Date(r.contacted_at), 'dd/MM/yyyy') : 'not yet'}
                      </span>
                    </label>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default InterestList;
